import { Component, OnInit, Input } from '@angular/core';
import { MessageService, MenuItem } from 'primeng/api';
import { AppDataService, PartnerProfileService, XpressUserService, MerchantUserService } from 'app/services';
import { XpressUserRoleEnum, MerchantUserRolesEnum } from 'app/models';

enum PartnerProfileTab {
    Info = '1',
    Security = '2',
    Email = '3'
}

@Component({
    selector: 'partner-profile',
    templateUrl: './partner-profile.component.html',
    styleUrls: ['./partner-profile.component.scss']
})
export class PartnerProfileComponent implements OnInit {
    @Input()
    public xpressUserRoleId: XpressUserRoleEnum = XpressUserRoleEnum.None;

    @Input()
    public userId: number = 0;

    @Input()
    public onHide: () => void = () => { };

    @Input()
    public onSave: () => void = () => { };

    public profile: {
        UserId: number;
        PartnerId: number;
        FirstName: string | null;
        LastName: string | null;
        Phone: string | null;
        Email: string;
        ImageUrl: string | null;
        RoleId: number | null;
        LocationId: number | null;
        Locations: any[];
    } = {
            UserId: 0,
            PartnerId: 0,
            FirstName: '',
            LastName: '',
            Phone: '',
            Email: '',
            ImageUrl: '',
            RoleId: null,
            LocationId: null,
            Locations: []
        };

    public roles: Array<any> = [{ Id: 0, Name: 'None', disabled: true }];
    public userRoleName = this.roles[0].Name;

    private merchantRoles = [
        { Id: MerchantUserRolesEnum.Cashier, Name: 'Cashier' },
        { Id: MerchantUserRolesEnum.Dispatcher, Name: 'Dispatcher' },
        { Id: MerchantUserRolesEnum.StoreManager, Name: 'StoreManager' },
        { Id: MerchantUserRolesEnum.Root, Name: 'Owner' }
    ];

    public locations: Array<any> = [{ Id: 0, Name: 'None', disabled: true }];

    public currentPassword: string = '';
    public newPassword: string = '';
    public confirmPassword: string = '';

    public menuItems: MenuItem[] = [
        {
            id: PartnerProfileTab.Info,
            label: 'My Profile'
        },
        {
            id: PartnerProfileTab.Security,
            label: 'Security'
        },
        {
            id: PartnerProfileTab.Email,
            label: 'Email Preferences'
        }
    ];

    public partnerProfileTab = PartnerProfileTab;
    public activeItem: MenuItem = this.menuItems[0];

    public isSpinnerVisible: boolean = true;

    constructor(
        private messageService: MessageService,
        public appData: AppDataService,
        private partnerProfileService: PartnerProfileService,
        private xpressUserService: XpressUserService,
        private merchantUserService: MerchantUserService) {
    }

    ngOnInit(): void {
        this.getPartnerProfile();
    }

    private getPartnerProfile(): void {
        const dto = {
            XpressUserRoleId: this.xpressUserRoleId,
            UserId: this.userId
        };

        this.partnerProfileService.getPartnerProfile(dto, this.messageService)
            .then(data => {
                if (!data) {
                    return;
                }

                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.profile = data;

                const roleId = this.profile.RoleId;
                const locationId = this.profile.LocationId;

                // Fill merchant roles
                if (this.xpressUserRoleId === XpressUserRoleEnum.Merchant) {
                    if (this.profile.RoleId) {
                        this.roles = this.merchantRoles; // Remove None option
                    }
                    else {
                        this.roles = this.roles.concat(this.merchantRoles);
                    }
                }

                // Find role name
                for (const role of this.roles) {
                    if (roleId === role.Id) {
                        this.userRoleName = role.Name;
                        break;
                    }
                }

                // Fill locations
                if (this.profile.LocationId) {
                    this.locations = this.profile.Locations; // Remove None option
                }
                else {
                    this.locations = this.locations.concat(this.profile.Locations);
                }

                // Restore values (dropdown may reset them on [options] changing)
                window.setTimeout(() => {
                    this.profile.RoleId = roleId;
                    this.profile.LocationId = locationId;
                }, 0);

                this.isSpinnerVisible = false;
            });
    }

    public onSideBarHide(): void {
        this.onHide();
    }

    public onActiveItemChange(item: any): void {
        this.activeItem = item;
    }

    public handleUploadClick(): void {
        document.getElementById('idImgInput')?.click();
    }

    public handleImageChange(event: Event) {
        const target = event.target as HTMLInputElement;

        let file: File | null = null;

        if (target.files) {
            file = target.files[0];
        }

        if (!file) {
            return;
        }

        // Chech max file size 2MB
        if (file.size > 2097152) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'This file is bigger than 2MB.' });
            return;
        }

        const reader = new FileReader();

        reader.onload = (e) => {
            if (e?.target?.result) {
                // Base64 string
                this.profile.ImageUrl = e.target.result.toString();
            }
        };

        reader.readAsDataURL(file);
    }

    public changePassword(): void {
        const dto = {
            OldPassword: this.currentPassword,
            NewPassword: this.newPassword,
            ConfirmPassword: this.confirmPassword
        };

        let promise: Promise<any> = Promise.resolve();

        if (this.xpressUserRoleId === XpressUserRoleEnum.Merchant) {
            promise = this.merchantUserService.changePassword(dto, this.messageService);
        }
        else {
            promise = this.xpressUserService.changePassword(dto, this.messageService);
        }

        promise.then(data => {
            if (!data) {
                return;
            }

            if (!data.Success) {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                return;
            }

            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Password successfully changed' });
        });
    }

    public onSaveClick(): void {
        const dto = {
            XpressUserRoleId: this.xpressUserRoleId,
            UserId: this.profile.UserId,
            FirstName: this.profile.FirstName,
            LastName: this.profile.LastName,
            Phone: this.profile.Phone,
            Email: this.profile.Email,
            ImageUrl: this.profile.ImageUrl,
            RoleId: this.profile.RoleId,
            LocationId: this.profile.LocationId
        };

        this.partnerProfileService.updatePartnerProfile(dto, this.messageService).then(data => {
            if (!data) {
                return;
            }

            if (!data.Success) {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                return;
            }

            this.onSave();
        });
    }
}
